// src/services/storage/sw.js
// Service worker registration, update lifecycle, and message bridge.
// Tile pre-caching requests go through here to the SW cache layer.

import { TileRegistry } from './idb.js';

let _registration = null;

// ── Register ──────────────────────────────────────────────────────
export async function registerSW() {
  if (!('serviceWorker' in navigator)) {
    console.warn('[SW] Not supported');
    return null;
  }
  try {
    _registration = await navigator.serviceWorker.register('/sw.js', {
      scope:          '/',
      updateViaCache: 'none',   // always check for new SW
    });

    // Activate a waiting SW immediately (e.g. after install)
    if (_registration.waiting) {
      _registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    // Poll for updates every 60s while the tab is open
    setInterval(() => _registration?.update(), 60_000);

    // Detect when a new SW has installed and is waiting
    _registration.addEventListener('updatefound', () => {
      const worker = _registration.installing;
      worker?.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          window.dispatchEvent(new CustomEvent('sw-update-available'));
          // Auto-skip waiting so new SW activates immediately
          worker.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    });

    // Bridge SW → app messages
    navigator.serviceWorker.addEventListener('message', _handleMessage);

    console.info('[SW] Registered:', _registration.scope);
    return _registration;
  } catch (err) {
    console.error('[SW] Registration failed:', err);
    return null;
  }
}

export function getRegistration() {
  return _registration;
}

// ── Apply update ──────────────────────────────────────────────────
export function applyUpdate() {
  const waiting = _registration?.waiting;
  if (waiting) {
    waiting.postMessage({ type: 'SKIP_WAITING' });
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    }, { once: true });
  }
}

// ── Send to SW ────────────────────────────────────────────────────
export function sendToSW(message) {
  const ctrl = navigator.serviceWorker?.controller;
  if (ctrl) ctrl.postMessage(message);
}

// ── Tile pre-caching ──────────────────────────────────────────────
export async function prefetchTiles(tileUrls) {
  sendToSW({ type: 'PREFETCH_TILES', urls: tileUrls });
  // Register in IDB so we know what's been requested
  await Promise.all(tileUrls.map((url) => TileRegistry.register(url)));
  return tileUrls.length;
}

// Generate tile URLs for a bounding box (used by MapPage)
export function getTileUrlsForArea(lat, lon, radiusMiles = 10, zooms = [10, 11, 12, 13]) {
  const urls = [];
  const deg  = radiusMiles / 69;
  for (const z of zooms) {
    const { x: x0, y: y0 } = _latLonToTile(lat - deg, lon - deg, z);
    const { x: x1, y: y1 } = _latLonToTile(lat + deg, lon + deg, z);
    for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) {
      for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) {
        urls.push(`https://tile.openstreetmap.org/${z}/${x}/${y}.png`);
      }
    }
  }
  return urls;
}

// ── Message bridge ────────────────────────────────────────────────
function _handleMessage(e) {
  const { type, ...detail } = e.data ?? {};
  const events = {
    API_UPDATED:       'na-api-updated',
    TILES_CACHED:      'na-tiles-cached',
    QUEUE_FLUSHED:     'na-queue-flushed',
    BACKGROUND_SYNC_COMPLETE: 'na-sync-complete',
    NAVIGATE_TO:       'na-navigate',
  };
  const name = events[type];
  if (name) window.dispatchEvent(new CustomEvent(name, { detail }));
}

function _latLonToTile(lat, lon, z) {
  const n = Math.pow(2, z);
  const x = Math.floor((lon + 180) / 360 * n);
  const y = Math.floor(
    (1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI)
    / 2 * n
  );
  return { x, y };
}
